import React from 'react';

// Styles & Assets
import styles from './DayAdminDialog.module.css';

// Services
import { getNonTakenValue, getTakenValue } from '../../../services/Pharmacy';
import { sortArray } from '../../../services/sort';
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useState } from 'react';
import useHttp from '../../../hooks/useHttp';


// Components
import MedicineAdmin from '../MedicineAdmin/MedicineAdmin';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';

// Interfaces
import { DrugAdministrationBody, DrugAdministrationList, DrugOption } from '../../../interfaces/Drug';

// Languages
import { useTranslation } from 'react-i18next';

const DayAdminDialog: React.FC<{
    drugs: DrugAdministrationList[]
    date: string
    showDialog: React.Dispatch<React.SetStateAction<boolean>>
    handleSetDrugs: (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => void
    handleSetOptionalDrugs: (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => void
}> = ({ drugs, date, showDialog, handleSetDrugs, handleSetOptionalDrugs }) => {

    const { t } = useTranslation();
    const { sendRequest } = useHttp();

    const defaultDrugOptions: DrugOption[] = [];
    drugs.forEach(drug => {
        sortArray(Object.keys(drug.hours)).forEach(hour => {
            if (drug.hours[hour][date] !== undefined) {
                const administrations = drug.hours[hour][date];
                defaultDrugOptions.push({
                    drugID: drug.drugId,
                    drugName: drug.drugName + ' - ' + hour,
                    hour: hour,
                    administrations: administrations,
                    taken: getTakenValue(administrations[0]) ?? false,
                    professionalAdmin: false,
                    counter: administrations[0].rescue_dose_number ?? 0,
                    forgotten: getNonTakenValue(administrations[0]),
                    comment: administrations[0].cancellation_reason ?? '',
                });
            }
        });
    });

    const [drugsOptions, setDrugsOptions] = useState<DrugOption[]>(defaultDrugOptions);

    const handleConfirm = () => {
        drugsOptions.forEach(drugOption => {
            const body: DrugAdministrationBody = {
                rescue_dose: drugOption.counter === 0 ? false : true,
                rescue_dose_number: drugOption.counter,
                status: drugOption.taken ?
                    'administred' : drugOption.forgotten ?
                        'forgotten' : 'cancelled',
                cancellation_reason: drugOption.forgotten ?
                    null : drugOption.comment
            };
            sendRequest({
                url: getEnvironmentApiUrl() + 'drug_administration/' + drugOption.administrations[0].id,
                method: 'PUT',
                body
            }, () => handleUpdateTaken(body, drugOption.drugID, drugOption.hour, drugOption.administrations.length > 0 ? drugOption.administrations[0].optional || false : false));
        });
        showDialog(false);

    };

    const handleUpdateTaken = (body: DrugAdministrationBody, id: number | undefined, hour: string, optional: boolean) => {
        const updateDrugs = (oldDrugs: DrugAdministrationList[]) => {
            const newDrugs = [...oldDrugs];
            return newDrugs.map(oldDrug => {
                if (oldDrug.drugId !== id) return oldDrug;
                const newDrug = { ...oldDrug };
                newDrug.hours[hour][date][0].rescue_dose = body.rescue_dose;
                newDrug.hours[hour][date][0].rescue_dose_number = body.rescue_dose_number;
                newDrug.hours[hour][date][0].status = body.status;
                newDrug.hours[hour][date][0].cancellation_reason = body.cancellation_reason;
                return newDrug;
            });
        };
        !optional ? handleSetDrugs(updateDrugs) : handleSetOptionalDrugs(updateDrugs);
    };

    const handleChangeDrugOption = (drugID: DrugOption['drugID']) => (optionAttributes: Partial<DrugOption>) => {
        setDrugsOptions(oldDrugOptions => oldDrugOptions.map(option => {
            if (option.drugID !== drugID) return option;
            return {
                ...option,
                ...optionAttributes
            }
        }))
    }

    return (
        <div className='dialog'>
            <h1 className='dialogTitle'>
                {t('administration')}
            </h1>
            <h2 className='dialogSubTitle' style={{ marginBottom: '3rem' }}>
                {date}
            </h2>
            <div className={styles.medicinesContainer}>
                {drugsOptions.map((drugOption, index) =>
                    <div key={drugOption.drugName} >
                        <MedicineAdmin
                            drugOption={drugOption}
                            handleChangeDrugOption={handleChangeDrugOption(drugOption.drugID)}
                        />
                        {index + 1 === drugsOptions.length ? null : <hr className={styles.horizontalLine}></hr>}
                    </div>
                )}
            </div>
            <CancelConfirmButtons
                onCancel={() => showDialog(false)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default DayAdminDialog;